import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function CGU() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'fr';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <button
          onClick={() => navigate('/')}
          className="text-primary-600 hover:text-primary-700 font-semibold mb-6"
        >
          ← {lang === 'fr' ? 'Retour' : 'Back'}
        </button>

        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          {lang === 'fr' ? "Conditions Générales d'Utilisation" : 'Terms of Use'}
        </h1>

        {lang === 'fr' ? (
          <>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong>Dernière mise à jour :</strong> 31 janvier 2026
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Objet</h2>
              <p className="text-gray-700 mb-4">
                Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») ont pour objet de définir les 
                modalités et conditions d'utilisation de la plateforme ProjectBoost (ci-après « la Plateforme »), 
                éditée par BEL-AN Prod.
              </p>
              <p className="text-gray-700 mb-4">
                L'utilisation de la Plateforme implique l'acceptation pleine et entière des présentes CGU.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Description des services</h2>
              <p className="text-gray-700 mb-4">
                ProjectBoost propose une plateforme en ligne permettant de :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Réaliser des diagnostics de projets via un questionnaire structuré</li>
                <li>Générer des rapports d'analyse détaillés</li>
                <li>Accéder à une bibliothèque de ressources en management de projet et leadership</li>
                <li>Télécharger des rapports PDF (selon abonnement)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Accès à la Plateforme</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1. Inscription</h3>
              <p className="text-gray-700 mb-4">
                L'accès à certaines fonctionnalités de la Plateforme nécessite la création d'un compte utilisateur. 
                Vous vous engagez à fournir des informations exactes et à les maintenir à jour.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2. Identifiants</h3>
              <p className="text-gray-700 mb-4">
                Vos identifiants de connexion sont personnels et confidentiels. Vous êtes responsable de leur 
                conservation et de toute utilisation qui en serait faite.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3. Types d'abonnement</h3>
              <p className="text-gray-700 mb-4">
                La Plateforme propose trois types d'abonnement :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Free :</strong> 1 diagnostic gratuit, consultation du rapport uniquement</li>
                <li><strong>Silver (29,99€/mois) :</strong> 3 diagnostics/mois, téléchargement PDF, historique illimité</li>
                <li><strong>Gold (49,99€/mois) :</strong> 7 diagnostics/mois, bibliothèque complète, tous les avantages</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Obligations de l'utilisateur</h2>
              <p className="text-gray-700 mb-4">
                En utilisant la Plateforme, vous vous engagez à :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Respecter les présentes CGU</li>
                <li>Ne pas utiliser la Plateforme à des fins illégales ou non autorisées</li>
                <li>Ne pas tenter d'accéder à des zones sécurisées de la Plateforme</li>
                <li>Ne pas diffuser de contenu illicite, diffamatoire ou contraire aux bonnes mœurs</li>
                <li>Respecter les droits de propriété intellectuelle de BEL-AN Prod et du Dr. Arthur MOMO KOUNTCHOU</li>
                <li>Ne pas télécharger, copier ou distribuer le contenu de la bibliothèque sans autorisation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Propriété intellectuelle</h2>
              <p className="text-gray-700 mb-4">
                L'ensemble des éléments de la Plateforme (textes, images, logos, code source, algorithmes, etc.) 
                sont la propriété exclusive de BEL-AN Prod et protégés par le droit d'auteur.
              </p>
              <p className="text-gray-700 mb-4">
                Les livres disponibles dans la bibliothèque sont la propriété intellectuelle du Dr. Arthur MOMO KOUNTCHOU. 
                Leur consultation est strictement réservée à un usage personnel et non commercial. Toute reproduction, 
                distribution ou exploitation commerciale est interdite.
              </p>
              <p className="text-gray-700 mb-4">
                Les rapports de diagnostic générés sont votre propriété, mais ne peuvent être revendus ou exploités 
                commercialement sans mention de la source.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Responsabilités</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.1. Responsabilité de BEL-AN Prod</h3>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod s'efforce d'assurer l'accès et le bon fonctionnement de la Plateforme. Toutefois, 
                BEL-AN Prod ne peut garantir :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>La disponibilité permanente de la Plateforme</li>
                <li>L'absence d'erreurs ou de bugs</li>
                <li>L'adéquation des services à vos besoins spécifiques</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.2. Nature des diagnostics</h3>
              <p className="text-gray-700 mb-4">
                Les diagnostics fournis par ProjectBoost sont des outils d'aide à la décision basés sur vos réponses. 
                Ils ne constituent pas :
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Des audits de conformité officiels</li>
                <li>Des conseils professionnels en management</li>
                <li>Des garanties de succès de projet</li>
              </ul>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod ne peut être tenu responsable des décisions prises sur la base de ces diagnostics.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.3. Responsabilité de l'utilisateur</h3>
              <p className="text-gray-700 mb-4">
                Vous êtes seul responsable de l'utilisation que vous faites de la Plateforme et des conséquences 
                qui en découlent. Vous vous engagez à indemniser BEL-AN Prod de tout préjudice résultant d'une 
                utilisation non conforme aux présentes CGU.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Données personnelles</h2>
              <p className="text-gray-700 mb-4">
                Le traitement de vos données personnelles est effectué conformément à notre{" "}
                <a href="/confidentialite" className="text-primary-600 hover:underline">Politique de Confidentialité</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Résiliation</h2>
              <p className="text-gray-700 mb-4">
                Vous pouvez résilier votre compte à tout moment depuis votre tableau de bord ou en nous contactant.
              </p>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod se réserve le droit de suspendre ou supprimer votre compte en cas de violation des 
                présentes CGU, sans préavis ni indemnité.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Modifications des CGU</h2>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs 
                seront informés des modifications importantes par email ou notification sur la Plateforme.
              </p>
              <p className="text-gray-700 mb-4">
                La poursuite de l'utilisation de la Plateforme après modification vaut acceptation des nouvelles CGU.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Droit applicable et juridiction</h2>
              <p className="text-gray-700 mb-4">
                Les présentes CGU sont soumises au droit français. En cas de litige, et à défaut de résolution 
                amiable, les tribunaux français seront seuls compétents.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Contact</h2>
              <p className="text-gray-700 mb-4">
                Pour toute question concernant les CGU :
              </p>
              <p className="text-gray-700 mb-2">
                Email : <a href="mailto:ace.direction22@gmail.com" className="text-primary-600 hover:underline">ace.direction22@gmail.com</a>
              </p>
            </section>
          </>
        ) : (
          <>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
              <p className="text-sm text-blue-800">
                <strong>Last updated:</strong> January 31, 2026
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Purpose</h2>
              <p className="text-gray-700 mb-4">
                These Terms of Use (hereinafter "Terms") aim to define the terms and conditions for using the 
                ProjectBoost platform (hereinafter "the Platform"), published by BEL-AN Prod.
              </p>
              <p className="text-gray-700 mb-4">
                Use of the Platform implies full acceptance of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. Service Description</h2>
              <p className="text-gray-700 mb-4">
                ProjectBoost offers an online platform allowing you to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Perform project diagnostics via a structured questionnaire</li>
                <li>Generate detailed analysis reports</li>
                <li>Access a library of project management and leadership resources</li>
                <li>Download PDF reports (depending on subscription)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Platform Access</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1. Registration</h3>
              <p className="text-gray-700 mb-4">
                Access to certain Platform features requires creating a user account. You agree to provide 
                accurate information and keep it up to date.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2. Credentials</h3>
              <p className="text-gray-700 mb-4">
                Your login credentials are personal and confidential. You are responsible for their safekeeping 
                and any use made of them.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3. Subscription Types</h3>
              <p className="text-gray-700 mb-4">
                The Platform offers three subscription types:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li><strong>Free:</strong> 1 free diagnostic, report consultation only</li>
                <li><strong>Silver ($29.99/month):</strong> 3 diagnostics/month, PDF download, unlimited history</li>
                <li><strong>Gold ($49.99/month):</strong> 7 diagnostics/month, full library, all benefits</li>
              </ul>
              <p className="text-gray-700 mb-4">
                A single diagnostic can also be purchased for $15 with no commitment.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. User Obligations</h2>
              <p className="text-gray-700 mb-4">
                By using the Platform, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Comply with these Terms</li>
                <li>Not use the Platform for illegal or unauthorized purposes</li>
                <li>Not attempt to access secured areas of the Platform</li>
                <li>Not distribute unlawful, defamatory or offensive content</li>
                <li>Respect the intellectual property rights of BEL-AN Prod and Dr. Arthur MOMO KOUNTCHOU</li>
                <li>Not download, copy or distribute library content without authorization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Intellectual Property</h2>
              <p className="text-gray-700 mb-4">
                All Platform elements (texts, images, logos, source code, algorithms, etc.) are the exclusive 
                property of BEL-AN Prod and protected by copyright.
              </p>
              <p className="text-gray-700 mb-4">
                Books available in the library are the intellectual property of Dr. Arthur MOMO KOUNTCHOU. 
                Their consultation is strictly reserved for personal, non-commercial use. Any reproduction, 
                distribution or commercial exploitation is prohibited.
              </p>
              <p className="text-gray-700 mb-4">
                Generated diagnostic reports are your property, but cannot be resold or commercially exploited 
                without mentioning the source.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Liability</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.1. BEL-AN Prod Liability</h3>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod strives to ensure Platform access and proper functioning. However, BEL-AN Prod 
                cannot guarantee:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Permanent Platform availability</li>
                <li>Absence of errors or bugs</li>
                <li>Suitability of services to your specific needs</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.2. Nature of Diagnostics</h3>
              <p className="text-gray-700 mb-4">
                Diagnostics provided by ProjectBoost are decision-support tools based on your responses. 
                They do not constitute:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Official compliance audits</li>
                <li>Professional management advice</li>
                <li>Project success guarantees</li>
              </ul>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod cannot be held liable for decisions made based on these diagnostics.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.3. User Liability</h3>
              <p className="text-gray-700 mb-4">
                You are solely responsible for your use of the Platform and its consequences. You agree to 
                indemnify BEL-AN Prod for any damage resulting from use not in compliance with these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Personal Data</h2>
              <p className="text-gray-700 mb-4">
                Processing of your personal data is carried out in accordance with our{" "}
                <a href="/confidentialite" className="text-primary-600 hover:underline">Privacy Policy</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Termination</h2>
              <p className="text-gray-700 mb-4">
                You can terminate your account at any time from your dashboard or by contacting us.
              </p>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod reserves the right to suspend or delete your account in case of violation of 
                these Terms, without notice or compensation.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Terms Modifications</h2>
              <p className="text-gray-700 mb-4">
                BEL-AN Prod reserves the right to modify these Terms at any time. Users will be informed 
                of significant changes by email or notification on the Platform.
              </p>
              <p className="text-gray-700 mb-4">
                Continued use of the Platform after modification constitutes acceptance of the new Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Applicable Law and Jurisdiction</h2>
              <p className="text-gray-700 mb-4">
                These Terms are governed by French law. In case of dispute, and failing amicable resolution, 
                French courts shall have sole jurisdiction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">11. Contact</h2>
              <p className="text-gray-700 mb-4">
                For any questions regarding the Terms:
              </p>
              <p className="text-gray-700 mb-2">
                Email: <a href="mailto:ace.direction22@gmail.com" className="text-primary-600 hover:underline">ace.direction22@gmail.com</a>
              </p>
            </section>
          </>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>© 2026 BEL-AN Prod - ProjectBoost. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
        </div>
      </div>
    </div>
  );
}

export default CGU;
